import { inject } from 'mobx-react';
import * as React from 'react';

import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { ErrorBoundary } from 'bernie-view';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';
import { ViewExtraLarge, ViewLarge, ViewMedium, Viewport, ViewSmall } from 'bernie-view';

import { DisplayAd } from 'constants/ads';
import { DisplayAdModel } from 'models/common/ads-types';
import { ExtendedContextStore } from 'types/flex-definitions';
import { PageName } from 'constants/pages';

import { MesoDisplayAd } from './meso-display-ad';
import { MesoTrigger } from './meso-trigger';
import { SwxlFooter } from 'components/common/footer/swxl-footer';

interface MesoFooterProps {
  destination: string;
  pageName: string;
  isDateless: boolean;
  context?: ExtendedContextStore;
}

const breakpointsFrameworkExperiment = 'MeSo_PWA_Breakpoints_Framework';

@withLocalization
@inject('context')
export class MesoFooter extends React.Component<MesoFooterProps & LocalizedComponentProps> {
  public render() {
    const { pageName, isDateless, context } = this.props;

    return pageName === PageName.SEARCH ||
      pageName === PageName.SEARCH_PACKAGE_HC ||
      pageName === PageName.SEARCH_PACKAGE_FH ||
      pageName === PageName.SEARCH_PACKAGE_FHC ? (
      <HSRFooter context={context} />
    ) : (
      <HISFooter isDateless={isDateless} context={context} />
    );
  }
}

interface FooterProps {
  isDateless?: boolean;
  context?: ExtendedContextStore;
}

const showDisplayAdComponent = (pageName: PageName) => (ads: DisplayAdModel[]) => <DisplayAdComponent ads={ads} pageName={pageName} />;

const showDisplayAdComponentHSR = showDisplayAdComponent(PageName.SEARCH);

const HSRFooter: React.FunctionComponent<FooterProps> = () => (
  <div className="uitk-grid all-grid-align-center all-t-padding-six">
    <AbacusExperiment name={breakpointsFrameworkExperiment}>
      <AbacusControl>
        <Viewport>
          <ViewSmall>
            <MesoDisplayAd uci="CM4" />
          </ViewSmall>
          <ViewMedium>{showDisplayAdComponentHSR([DisplayAd.HSR_FOOTER_M_CM2, DisplayAd.HSR_FOOTER_M_CM1])}</ViewMedium>
          <ViewLarge>{showDisplayAdComponentHSR([DisplayAd.HSR_FOOTER_L_CM2, DisplayAd.HSR_FOOTER_L_CM1])}</ViewLarge>
          <ViewExtraLarge>
            <div className="uitk-cell all-cell-1-1 all-b-padding-six">
              {showDisplayAdComponentHSR([DisplayAd.HSR_FOOTER_XL_CM2, DisplayAd.HSR_FOOTER_XL_CM1])}
            </div>
          </ViewExtraLarge>
        </Viewport>
      </AbacusControl>
      <AbacusVariant bucket={1}>
        <section className="uitk-grid all-grid-align-center">
          <meso-media-query uci="CM1">
            <div>
              <meso-display-ad class="ad-div display-ad" uci="CM1" />
            </div>
          </meso-media-query>
          <meso-media-query uci="CM2">
            <div>
              <meso-display-ad class="ad-div display-ad" uci="CM2" />
            </div>
          </meso-media-query>
          <meso-media-query uci="CM4">
            <div>
              <meso-display-ad class="ad-div display-ad" uci="CM4" />
            </div>
          </meso-media-query>
        </section>
      </AbacusVariant>
    </AbacusExperiment>
  </div>
);

const showDisplayAdComponentHIS = showDisplayAdComponent(PageName.INFOSITE);

const HISFooter: React.FunctionComponent<FooterProps> = ({ isDateless, context }) => (
  <AbacusExperiment name={breakpointsFrameworkExperiment}>
    <AbacusControl>
      <Viewport>
        <ViewSmall>
          <div className="uitk-grid all-grid-align-center all-y-padding-four all-x-padding-four">
            {isDateless && <SwxlFooter context={context} className="uitk-cell all-cell-1-1 all-b-padding-four swxl" />}
            <div className="uitk-cell all-cell-1-1 all-b-padding-four">
              <div className="uitk-grid all-grid-align-center">
                <MesoDisplayAd uci={DisplayAd.HIS_FOOTER_S_CM6.uci} mediaquery={DisplayAd.HIS_FOOTER_S_CM6.mediaquery} />
              </div>
            </div>
            <MesoTrigger pageName={PageName.INFOSITE} />
          </div>
        </ViewSmall>
        <ViewMedium>
          <div className="uitk-grid all-grid-align-center all-y-padding-four">
            {isDateless && <SwxlFooter context={context} className="uitk-cell all-cell-1-1 all-b-padding-four" />}
            <div className="uitk-cell all-cell-1-1 all-b-padding-four">
              {showDisplayAdComponentHIS([DisplayAd.HIS_FOOTER_M_CM3, DisplayAd.HIS_FOOTER_M_CM2])}
            </div>
          </div>
        </ViewMedium>
        <ViewLarge>
          <div className="uitk-grid all-grid-align-center all-y-padding-four all-cell-1-2">
            <div className="uitk-cell all-cell-1-1 all-b-padding-four">{showDisplayAdComponentHIS([DisplayAd.HIS_FOOTER_L_XL_CM1])}</div>
            {isDateless && <SwxlFooter context={context} className="uitk-cell all-r-padding-four uitk-flex-grow-1" />}
          </div>
        </ViewLarge>
      </Viewport>
    </AbacusControl>
    <AbacusVariant bucket={1}>
      <div className="uitk-grid all-grid-align-center all-y-padding-four all-cell-1-1">
        <div className="uitk-cell all-cell-1-1 all-b-padding-four">
          <section className="uitk-grid all-grid-align-center">
            <meso-display-ad class="ad-div display-ad" uci="CM1" />
            <meso-media-query uci="CM2">
              <div>
                <meso-display-ad class="ad-div display-ad" uci="CM2" />
              </div>
            </meso-media-query>
            <meso-media-query uci="CM3">
              <div>
                <meso-display-ad class="ad-div display-ad" uci="CM3" />
              </div>
            </meso-media-query>
            <meso-display-ad class="ad-div display-ad" uci="CM6" />
          </section>
        </div>
        <Viewport>
          <ViewSmall>
            <MesoTrigger pageName={PageName.INFOSITE} />
          </ViewSmall>
          <ViewMedium>{isDateless && <SwxlFooter context={context} className="uitk-cell all-cell-1-1 all-b-padding-four" />}</ViewMedium>
          <ViewLarge>{isDateless && <SwxlFooter context={context} className="uitk-cell all-r-padding-four uitk-flex-grow-1" />}</ViewLarge>
        </Viewport>
      </div>
    </AbacusVariant>
  </AbacusExperiment>
);

interface DisplayAdComponentProps {
  ads: DisplayAdModel[];
  pageName: string;
}

const DisplayAdComponent: React.FunctionComponent<DisplayAdComponentProps> = ({ ads, pageName }) => (
  <ErrorBoundary label="meso.footer">
    <section className="uitk-grid all-grid-align-center">
      {ads.map(({ mediaquery, uci }) => (
        <MesoDisplayAd key={uci} uci={uci} mediaquery={mediaquery} />
      ))}
    </section>
    <MesoTrigger pageName={pageName} />
  </ErrorBoundary>
);
